/* eslint-disable no-unused-vars */
import {
  InvoiceAggregate,
  InvoiceNumberFormatValidator,
  InvoiceNumberFormatValidatorSchema,
  RepositoryError,
} from "@invoiceddd/domain";
import { Context, Data, Effect, Layer, Option } from "effect";
import { InvoiceRepository, InvoiceRepositorySchema } from "../ports/InvoiceRepository";

/**
 * Use case errors
 */
export class GetInvoiceError extends Data.TaggedError("@/GetInvoiceUseCase/GetInvoiceError")<{
  readonly cause?: unknown;
  readonly message: string;
  readonly searchCriteria?: string;
}> {}

export class InvoiceNotFoundError extends Data.TaggedError(
  "@/GetInvoiceUseCase/InvoiceNotFoundError"
)<{
  readonly cause?: unknown;
  readonly message: string;
  readonly searchValue: string;
  readonly searchType: "orderId" | "invoiceNumber";
}> {}

export class InvalidSearchCriteriaError extends Data.TaggedError(
  "@/GetInvoiceUseCase/InvalidSearchCriteriaError"
)<{
  readonly cause?: unknown;
  readonly message: string;
  readonly providedCriteria?: string;
}> {}

/**
 * Input for getting an invoice
 */
export interface GetInvoiceInput {
  readonly orderId?: string;
  readonly invoiceNumber?: string;
  readonly returnType?: "json" | "pdf";
}

/**
 * Output from getting an invoice
 */
export interface GetInvoiceOutput {
  readonly invoice: InvoiceAggregate;
  readonly pdfBytes?: Uint8Array;
}

/**
 * Use case for retrieving a single invoice by various criteria.
 * This use case supports lookup by orderId or invoiceNumber and can
 * return either the domain object or PDF bytes based on request.
 */
export interface GetInvoiceUseCaseSchema {
  /**
   * Get an invoice by orderId or invoiceNumber
   * @param input - Search criteria and return type preferences
   * @returns Effect containing invoice data or error
   */
  readonly execute: (
    input: GetInvoiceInput
  ) => Effect.Effect<
    GetInvoiceOutput,
    GetInvoiceError | InvoiceNotFoundError | InvalidSearchCriteriaError | RepositoryError
  >;
}

/**
 * Context tag for GetInvoiceUseCase
 */
export class GetInvoiceUseCase extends Context.Tag("@/UseCases/GetInvoiceUseCase")<
  GetInvoiceUseCase,
  GetInvoiceUseCaseSchema
>() {}

/**
 * Implementation of GetInvoiceUseCase
 */
export const GetInvoiceUseCaseLive = Layer.effect(
  GetInvoiceUseCase,
  Effect.gen(function* () {
    const invoiceRepository = yield* InvoiceRepository;
    const invoiceNumberValidator = yield* InvoiceNumberFormatValidator;

    const implementation: GetInvoiceUseCaseSchema = {
      execute: (input: GetInvoiceInput) => {
        return Effect.gen(function* () {
          // Step 1: Validate search criteria
          yield* validateSearchCriteria(input, invoiceNumberValidator);

          // Step 2: Search for invoice based on provided criteria
          const invoiceOption = yield* searchInvoice(input, invoiceRepository);

          // Step 3: Check if invoice was found
          if (Option.isNone(invoiceOption)) {
            return yield* Effect.fail(createNotFoundError(input));
          }

          const invoice = invoiceOption.value;

          // Step 4: Create aggregate from domain data
          const invoiceAggregate = InvoiceAggregate.fromValidatedData(invoice);

          // Step 5: Prepare output based on return type
          let output: GetInvoiceOutput = {
            invoice: invoiceAggregate,
          };

          // Include PDF bytes only if explicitly requested
          if (input.returnType === "pdf") {
            if (!invoice.pdfBytes) {
              return yield* Effect.fail(
                new GetInvoiceError({
                  message: "Wanted to get PDF data for an invoice, but no PDF available. Badummz",
                  cause: invoice,
                })
              );
            }

            output = {
              ...output,
              pdfBytes: invoice.pdfBytes,
            };
          }

          return output;
        }).pipe(
          Effect.mapError(error => {
            if (
              error._tag === "@/GetInvoiceUseCase/InvoiceNotFoundError" ||
              error._tag === "@/GetInvoiceUseCase/InvalidSearchCriteriaError"
            ) {
              return error;
            }
            if (error._tag === "@/RepositoryError") {
              return error;
            }
            return new GetInvoiceError({
              message: `Failed to get invoice: ${String(error)}`,
              cause: error,
            });
          }),
          Effect.withSpan("UseCases.GetInvoice.execute", {
            attributes: {
              orderId: input.orderId,
              invoiceNumber: input.invoiceNumber,
              returnType: input.returnType ?? "json",
            },
          })
        );
      },
    };

    return GetInvoiceUseCase.of(implementation);
  })
);

/**
 * Validate search criteria
 */
const validateSearchCriteria = (
  input: GetInvoiceInput,
  validator: InvoiceNumberFormatValidatorSchema
): Effect.Effect<void, InvalidSearchCriteriaError> => {
  return Effect.gen(function* () {
    // Must provide either orderId or invoiceNumber
    if (!input.orderId && !input.invoiceNumber) {
      return yield* Effect.fail(
        new InvalidSearchCriteriaError({
          message: "Must provide either orderId or invoiceNumber",
        })
      );
    }

    // Cannot provide both orderId and invoiceNumber
    if (input.orderId && input.invoiceNumber) {
      return yield* Effect.fail(
        new InvalidSearchCriteriaError({
          message: "Cannot provide both orderId and invoiceNumber. Choose one search criterion.",
          providedCriteria: `orderId: ${input.orderId}, invoiceNumber: ${input.invoiceNumber}`,
        })
      );
    }

    // Validate orderId format if provided
    if (input.orderId) {
      if (input.orderId.trim().length === 0) {
        return yield* Effect.fail(
          new InvalidSearchCriteriaError({
            message: "Order ID cannot be empty",
            providedCriteria: `orderId: "${input.orderId}"`,
          })
        );
      }
    }

    // Validate invoiceNumber format if provided using the configurable validator
    if (input.invoiceNumber) {
      yield* validator.validate(input.invoiceNumber).pipe(
        Effect.mapError(
          error =>
            new InvalidSearchCriteriaError({
              message: `Invalid invoice number format: ${error.message}`,
              providedCriteria: `invoiceNumber: "${input.invoiceNumber}"`,
            })
        )
      );
    }

    // Validate return type if provided
    if (input.returnType && !["json", "pdf"].includes(input.returnType)) {
      return yield* Effect.fail(
        new InvalidSearchCriteriaError({
          message: `Invalid return type. Must be 'json' or 'pdf', but got '${input.returnType}'`,
          providedCriteria: `returnType: "${input.returnType}"`,
        })
      );
    }
  });
};

/**
 * Search for invoice using the provided criteria
 */
const searchInvoice = (
  input: GetInvoiceInput,
  repository: InvoiceRepositorySchema
): Effect.Effect<Option.Option<import("@invoiceddd/domain").Invoice>, RepositoryError> => {
  if (input.orderId) {
    return repository.findByOrderId(input.orderId);
  }

  if (input.invoiceNumber) {
    return repository.findByInvoiceNumber(input.invoiceNumber);
  }

  // This should never happen due to validation, but TypeScript requires it
  return Effect.succeed(Option.none());
};

/**
 * Create appropriate not found error based on search criteria
 */
const createNotFoundError = (input: GetInvoiceInput): InvoiceNotFoundError => {
  if (input.orderId) {
    return new InvoiceNotFoundError({
      message: `Invoice not found for order ID: ${input.orderId}`,
      searchValue: input.orderId,
      searchType: "orderId",
    });
  }

  if (input.invoiceNumber) {
    return new InvoiceNotFoundError({
      message: `Invoice not found for invoice number: ${input.invoiceNumber}`,
      searchValue: input.invoiceNumber,
      searchType: "invoiceNumber",
    });
  }

  // This should never happen due to validation
  return new InvoiceNotFoundError({
    message: "Invoice not found",
    searchValue: "unknown",
    searchType: "orderId",
  });
};
